import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllSpeciesForCareGuides } from '../services/api';
import { getSpeciesImage, getSpeciesGridImage, resolveCredit, buildAssetUrl } from '../utils/imageResolver';
import ImageAttribution from '../components/media/ImageAttribution';
import FroggySpinner from '../components/common/FroggySpinner';
import { createSpeciesSlug } from '../utils/slugUtils';
import { NO_PHOTO_SRC } from '../constants/noPhoto';
import { Helmet } from 'react-helmet-async';
import { formatScientificName } from '../utils/scientificName';
import { excerptText } from '../utils/excerpt';

// Safe text converter to prevent object rendering crashes
const safeText = (v) => (typeof v === 'string' ? v : (v == null ? '' : String(v)));

export default function CareGuidesPage() {
  const [careGuides, setCareGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadCareGuides = async () => {
      try {
        setLoading(true);
        const response = await getAllSpeciesForCareGuides();

        // Handle different response formats robustly
        const speciesList = Array.isArray(response) ? response : Array.isArray(response?.results) ? response.results : [];

        // Filter for species with published care guides (use care_guide_available flag)
        const publishedGuides = speciesList
          .filter(species => !!species && typeof species === 'object') // guard against null/undefined/primitives
          .filter(species => species.care_guide_available === true);

        setCareGuides(publishedGuides);
      } catch (err) {
        console.error('Error loading care guides:', err);
        setError('Failed to load care guides');
      } finally {
        setLoading(false);
      }
    };

    loadCareGuides();
  }, []);

  // Get species display name
  const getSpeciesDisplayName = (species) => {
    const genus = safeText(species?.genus);
    const speciesName = safeText(species?.species);
    const locality = safeText(species?.locality);

    const name = formatScientificName(genus, speciesName, locality, species?.species_type);

    return name || 'Unknown Species';
  };

  // Get common name if available
  const getCommonName = (species) => {
    const commonName = species?.common_names?.[0]?.name || species?.common_names?.[0] || species?.common_name || species?.commonName || '';
    return safeText(commonName);
  };

  // Get excerpt from care guide or overview
  const getExcerpt = (species) => {
    if (species?.care_guide_excerpt) {
      // Run 42: the server's excerpt is already cut (on a word, with ONE
      // ellipsis) — never cut mid-word or given a second ellipsis here.
      return excerptText(safeText(species.care_guide_excerpt), 200);
    }

    // Provide a default excerpt
    return 'Comprehensive care information for optimal husbandry and breeding.';
  };

  if (error) {
    return (
      <div className="page-container">
        <div className="alert alert-warning">
          <h4>Unable to Load Care Guides</h4>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Species Care Guides – Feeling Froggy</title>
        <meta
          name="description"
          content="Comprehensive care guides for frogs, amphibians, and vivarium species. Expert advice on habitat setup, feeding, temperature, humidity, and health monitoring."
        />
        {/* #136: this indexable page had NO canonical once the shell's
            conflicting root canonical was removed — give it its own */}
        <link rel="canonical" href="https://feelingfroggy.llc/care-guides" />
      </Helmet>

      {/* R5 B6: the index's card grid uses the site's container width
          (`care-guides-page`), not the 768px text-page measure — at 1280
          the cards sat in half the page. The intro keeps the text measure. */}
      <div className="page-container care-guides-page" data-care-guides-index="1">
        <header className="mb-5">
          <h1 className="ff-heading-h1" style={{ marginBottom: 'var(--ff-space-lg)' }}>
            Species Care Guides
          </h1>
          <p className="lead">
            Comprehensive care guides to help you provide the best environment for your animals.
            Each guide covers essential topics like habitat setup, feeding, temperature, humidity, and health monitoring.
          </p>
        </header>

        {loading ? (
          <div className="text-center py-5">
            <FroggySpinner size="lg" label="Loading care guides" />
          </div>
        ) : (
          <div className="row care-guides-grid">
            {careGuides.length === 0 ? (
              <div className="col-12">
                <div className="text-center py-5">
                  <h3>No Care Guides Available</h3>
                  <p className="text-muted">
                    Care guides are currently being developed. Check back soon for detailed species information.
                  </p>
                </div>
              </div>
            ) : (
              Array.isArray(careGuides) && careGuides.map((species) => {
              try {
                if (!species || !species.id) return null; // Guard against invalid data

                const slug = createSpeciesSlug(species);
                const displayName = getSpeciesDisplayName(species);
                const commonName = getCommonName(species);
                const excerpt = getExcerpt(species);

                // Robust image URL handling - ensure imageUrl is a string, not object
                let imageUrl = null;
                if (typeof species.imageUrl === 'string') {
                  imageUrl = species.imageUrl;
                } else {
                  // Use the same image mapping as species grid: cover_display_url -> cover_thumb_url -> fallback
                  imageUrl = getSpeciesGridImage(species) || getSpeciesImage(species);
                }
                // Batch 34 C6: a guide with no cover shows the app's
                // neutral no-photo tile (R10), never a blank grey box.
                const speciesImageUrl = imageUrl || NO_PHOTO_SRC;
                const noPhoto = !imageUrl;

                const credit = resolveCredit(species);
                return (
                <div key={species.id} className="col-md-6 col-lg-4 mb-4">
                  <div className="card h-100 care-guide-card">
                    <div className="care-guide-image-container" style={{ position: 'relative' }}>
                      <img
                        loading="lazy"
                        src={speciesImageUrl}
                        className="card-img-top care-guide-image"
                        alt={displayName}
                        data-no-photo={noPhoto ? '1' : undefined}
                        onError={(e) => {
                          if (!e.currentTarget.src.endsWith(NO_PHOTO_SRC)) {
                            e.currentTarget.src = NO_PHOTO_SRC;
                            e.currentTarget.setAttribute('data-no-photo', '1');
                          }
                        }}
                      />
                      {credit && (
                        <div style={{ position: 'absolute', left: 6, bottom: 6, maxWidth: 'calc(100% - 12px)' }}>
                          <ImageAttribution asset={credit} variant="pill" />
                        </div>
                      )}
                    </div>
                    <div className="card-body d-flex flex-column">
                      <h5 className="card-title">
                        <em>{displayName}</em>
                      </h5>
                      {commonName && (
                        <p className="text-muted small mb-2">{commonName}</p>
                      )}
                      <p className="card-text text-muted small flex-grow-1">
                        {excerpt}
                      </p>
                      <div className="mt-auto">
                        {/* Batch 34 C1: twenty filled "Read Care Guide" were a
                            wall of blue. A card's own act is a secondary at md
                            (36px), as "View Details" on /frogs. */}
                        <button
                          type="button"
                          className="ff-btn ff-btn--secondary"
                          data-care-guide-read
                          onClick={() => navigate(`/care-guides/${slug}`)}
                        >
                          Read Care Guide
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
              } catch (e) {
                console.error('CareGuide card error', {species, e});
                return null;
              }
            })
            )}
          </div>
        )}

        {!loading && careGuides.length > 0 && (
          <footer className="mt-5 pt-4 border-top">
            <div className="row">
              <div className="col-md-8">
                <h4>General Care Categories</h4>
                <div className="row">
                  <div className="col-sm-6">
                    <h6>Frogs & Amphibians</h6>
                    <ul className="small text-muted">
                      <li>Terrarium setup & ventilation</li>
                      <li>Temperature & humidity control</li>
                      <li>Feeding & nutrition</li>
                      <li>Health monitoring</li>
                    </ul>
                  </div>
                  <div className="col-sm-6">
                    <h6>Vivarium & Plants</h6>
                    <ul className="small text-muted">
                      <li>Safe plant selection</li>
                      <li>Lighting requirements</li>
                      <li>Substrate & drainage</li>
                      <li>Maintenance schedules</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="alert alert-info">
                  <small>
                    <strong>Need specific advice?</strong><br />
                    Our care guides are continuously updated based on the latest research and keeper experiences.
                  </small>
                </div>
              </div>
            </div>
          </footer>
        )}
      </div>

      <style jsx>{`
        /* R5 B6 — the index is a GRID page, not a text page: the full
           container (1280 less the page's gutters; 3 columns from 992px,
           2 from 768px, 1 on a phone — the cards' own col classes). The
           intro paragraph keeps the 768px reading measure. */
        .page-container.care-guides-page {
          max-width: var(--ff-container-xl, 1280px);
        }

        .care-guides-page > header .lead {
          max-width: var(--ff-container-md, 768px);
        }

        .care-guide-card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        /* I-02 close-out N4 — equal-height cards (48px spread measured).
           Same fix as the PDP .listings-grid (products.css, Cowork
           re-measure 2026-08-14): .card.h-100 never resolves because a
           block col in a WRAPPED flex row is not a definite-height
           containing block. Make the col the flex context and let the
           card flex to fill it; each col has exactly one in-flow child. */
        .care-guides-grid > [class*="col-"] {
          display: flex;
          flex-direction: column;
        }

        .care-guides-grid > [class*="col-"] > .care-guide-card {
          flex: 1 1 auto;
          height: auto;
        }

        .care-guide-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }

        .care-guide-image-container {
          height: 200px;
          overflow: hidden;
          background-color: var(--ff-surface-tertiary);
        }

        .care-guide-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .care-guide-card:hover .care-guide-image {
          transform: scale(1.05);
        }

        /* Batch 34 C6: the no-photo tile is shown WHOLE on its own pale
           ground (contain, never a cropped cover) and does not zoom. */
        .care-guide-image[data-no-photo] {
          object-fit: contain;
          background: var(--ff-surface-secondary, #f8f9fa);
        }

        .care-guide-card:hover .care-guide-image[data-no-photo] {
          transform: none;
        }
      `}</style>
    </>
  );
}